import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Plus, Trash2, Download, Calendar, Users, ChevronDown, ChevronUp, Cpu, DollarSign, X, Eye, GitCompare, Check } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

const TYPE_LABELS = {
  simulation_911: 'Simulation 911',
  rao_variant: 'Variante RAO',
  retroaction: 'Rétroaction',
};
const TYPE_COLORS = {
  simulation_911: '#2E6CF5',
  rao_variant: '#24B219',
  retroaction: '#F4621C',
};

const PracticeSessionsTab = ({ costPer1kTokens, groups }) => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [viewingSession, setViewingSession] = useState(null);
  const [sessionStats, setSessionStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [compareMode, setCompareMode] = useState(false);
  const [selectedForCompare, setSelectedForCompare] = useState([]);
  const [compareData, setCompareData] = useState(null);
  const [compareLoading, setCompareLoading] = useState(false);
  const [form, setForm] = useState({
    name: '',
    group_id: '',
    start_date: '',
    start_time: '08:00',
    end_date: '',
    end_time: '15:00',
  });

  const estimateCost = (tokens) => ((tokens / 1000) * costPer1kTokens).toFixed(2);

  const toggleCompareSelection = (id) => {
    setSelectedForCompare(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const runComparison = async () => {
    if (selectedForCompare.length < 2) { toast.error('Sélectionnez au moins 2 séances'); return; }
    setCompareLoading(true);
    try {
      const results = await Promise.all(
        selectedForCompare.map(id => axios.get(`${API}/admin/usage/practice-sessions/${id}/stats`))
      );
      setCompareData(results.map(r => r.data));
    } catch {
      toast.error('Erreur lors du chargement');
    } finally { setCompareLoading(false); }
  };

  const exitCompare = () => {
    setCompareMode(false);
    setSelectedForCompare([]);
    setCompareData(null);
  };

  const loadSessions = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await axios.get(`${API}/admin/usage/practice-sessions`);
      setSessions(resp.data);
    } catch (err) { console.warn('loadSessions failed:', err); } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadSessions(); }, [loadSessions]);

  const createSession = async (e) => {
    e.preventDefault();
    if (!form.name || !form.start_date || !form.end_date) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    try {
      // Convert local times to UTC ISO strings for proper comparison with UTC logs
      const startLocal = new Date(`${form.start_date}T${form.start_time}:00`);
      const endLocal = new Date(`${form.end_date}T${form.end_time}:00`);
      const start_time = startLocal.toISOString();
      const end_time = endLocal.toISOString();
      await axios.post(`${API}/admin/usage/practice-sessions`, {
        name: form.name,
        group_id: form.group_id,
        start_time,
        end_time,
      });
      toast.success('Séance créée');
      setShowForm(false);
      setForm({ name: '', group_id: '', start_date: '', start_time: '08:00', end_date: '', end_time: '15:00' });
      loadSessions();
    } catch {
      toast.error('Erreur lors de la création');
    }
  };

  const deleteSession = async (id, name) => {
    if (!window.confirm(`Supprimer la séance "${name}" ?`)) return;
    try {
      await axios.delete(`${API}/admin/usage/practice-sessions/${id}`);
      toast.success('Séance supprimée');
      if (viewingSession === id) { setViewingSession(null); setSessionStats(null); }
      loadSessions();
    } catch {
      toast.error('Erreur lors de la suppression');
    }
  };

  const viewStats = async (id) => {
    if (viewingSession === id) { setViewingSession(null); setSessionStats(null); return; }
    setViewingSession(id);
    setStatsLoading(true);
    try {
      const resp = await axios.get(`${API}/admin/usage/practice-sessions/${id}/stats`);
      setSessionStats(resp.data);
    } catch {
      toast.error('Erreur lors du chargement des statistiques');
    } finally { setStatsLoading(false); }
  };

  const exportCSV = async (id, name) => {
    try {
      const resp = await axios.get(`${API}/admin/usage/practice-sessions/${id}/export`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `seance_${name.replace(/\s/g, '_')}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV téléchargé');
    } catch {
      toast.error("Erreur lors de l'export");
    }
  };

  const formatDateTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleDateString('fr-CA', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'America/Toronto' }) + ' ' + d.toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Toronto' });
  };

  if (loading) {
    return <div className="flex items-center justify-center py-16"><div className="animate-pulse text-lg" style={{ color: '#2E6CF5' }}>Chargement...</div></div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
        <h2 className="text-2xl font-bold" style={{ color: '#002768' }} data-testid="practice-sessions-title">
          {compareMode ? 'Comparaison de séances' : 'Séances de pratique'}
        </h2>
        <div className="flex items-center gap-2">
          {!compareMode && sessions.length >= 2 && (
            <button
              onClick={() => { setCompareMode(true); setShowForm(false); setViewingSession(null); }}
              className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium hover:opacity-90 border-2"
              style={{ borderColor: '#7C3AED', color: '#7C3AED' }}
              data-testid="compare-mode-btn"
            >
              <GitCompare className="w-4 h-4" />
              Comparer
            </button>
          )}
          {compareMode && (
            <>
              <span className="text-sm text-gray-500">{selectedForCompare.length} sélectionnée(s)</span>
              <button
                onClick={runComparison}
                disabled={selectedForCompare.length < 2 || compareLoading}
                className="flex items-center gap-2 px-4 py-2 text-white rounded-lg font-medium hover:opacity-90 disabled:opacity-40"
                style={{ backgroundColor: '#7C3AED' }}
                data-testid="run-compare-btn"
              >
                {compareLoading ? 'Calcul...' : 'Voir la comparaison'}
              </button>
              <button
                onClick={exitCompare}
                className="flex items-center gap-1 px-3 py-2 rounded-lg text-gray-600 hover:bg-gray-100"
                data-testid="exit-compare-btn"
              >
                <X className="w-4 h-4" /> Annuler
              </button>
            </>
          )}
          {!compareMode && (
            <button
              onClick={() => setShowForm(!showForm)}
              className="flex items-center gap-2 px-4 py-2 text-white rounded-lg font-medium hover:opacity-90"
              style={{ backgroundColor: '#002768' }}
              data-testid="create-practice-session-btn"
            >
              {showForm ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
              {showForm ? 'Annuler' : 'Nouvelle séance'}
            </button>
          )}
        </div>
      </div>

      {/* Create Form */}
      {showForm && (
        <Card className="glass border-0 mb-6" style={{ borderLeft: '4px solid #2E6CF5' }}>
          <CardContent className="pt-6">
            <form onSubmit={createSession} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nom de la séance *</label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                    placeholder="Ex: Pratique 976 — 12 avril"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:outline-none"
                    style={{ '--tw-ring-color': '#2E6CF5' }}
                    required
                    data-testid="practice-session-name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Groupe (optionnel)</label>
                  <select
                    value={form.group_id}
                    onChange={(e) => setForm(f => ({ ...f, group_id: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:outline-none"
                    style={{ '--tw-ring-color': '#2E6CF5' }}
                    data-testid="practice-session-group"
                  >
                    <option value="">Tous les étudiants</option>
                    {groups.map(g => (
                      <option key={g.id} value={g.id}>{g.name} ({g.member_count} membres)</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date début *</label>
                  <input type="date" value={form.start_date} onChange={(e) => setForm(f => ({ ...f, start_date: e.target.value }))} className="w-full px-3 py-2 border rounded-lg" required data-testid="practice-start-date" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Heure début</label>
                  <input type="time" value={form.start_time} onChange={(e) => setForm(f => ({ ...f, start_time: e.target.value }))} className="w-full px-3 py-2 border rounded-lg" data-testid="practice-start-time" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date fin *</label>
                  <input type="date" value={form.end_date} onChange={(e) => setForm(f => ({ ...f, end_date: e.target.value }))} className="w-full px-3 py-2 border rounded-lg" required data-testid="practice-end-date" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Heure fin</label>
                  <input type="time" value={form.end_time} onChange={(e) => setForm(f => ({ ...f, end_time: e.target.value }))} className="w-full px-3 py-2 border rounded-lg" data-testid="practice-end-time" />
                </div>
              </div>
              <button
                type="submit"
                className="px-6 py-2 text-white rounded-lg font-medium hover:opacity-90"
                style={{ backgroundColor: '#2E6CF5' }}
                data-testid="save-practice-session"
              >
                Créer la séance
              </button>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Sessions List */}
      {sessions.length === 0 ? (
        <Card className="glass border-0">
          <CardContent className="py-12 text-center text-gray-400">
            <Calendar className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>Aucune séance de pratique enregistrée</p>
            <p className="text-sm mt-1">Créez une séance pour suivre les coûts d'une période de pratique</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {sessions.map(session => (
            <Card key={session.id} className="glass border-0">
              <CardContent className="pt-5 pb-4">
                <div className="flex items-start justify-between gap-4">
                  {compareMode && (
                    <button
                      onClick={() => toggleCompareSelection(session.id)}
                      className={`mt-1 w-6 h-6 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                        selectedForCompare.includes(session.id) ? 'text-white' : 'border-gray-300'
                      }`}
                      style={selectedForCompare.includes(session.id) ? { backgroundColor: '#7C3AED', borderColor: '#7C3AED' } : {}}
                      data-testid={`compare-check-${session.id}`}
                    >
                      {selectedForCompare.includes(session.id) && <Check className="w-4 h-4" />}
                    </button>
                  )}
                  <div className="flex-1">
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="text-lg font-bold" style={{ color: '#002768' }}>{session.name}</h3>
                      {session.group_name && (
                        <Badge className="text-white text-xs" style={{ backgroundColor: '#2E6CF5' }}>
                          <Users className="w-3 h-3 mr-1" />{session.group_name}
                        </Badge>
                      )}
                      {!session.group_id && (
                        <Badge variant="outline" className="text-xs">Tous les étudiants</Badge>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 mt-1">
                      <Calendar className="w-3.5 h-3.5 inline -mt-0.5 mr-1" />
                      {formatDateTime(session.start_time)} — {formatDateTime(session.end_time)}
                    </p>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => viewStats(session.id)}
                      className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium hover:opacity-90 text-white"
                      style={{ backgroundColor: viewingSession === session.id ? '#F4621C' : '#002768' }}
                      data-testid={`view-stats-${session.id}`}
                    >
                      {viewingSession === session.id ? <ChevronUp className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      {viewingSession === session.id ? 'Masquer' : 'Détails'}
                    </button>
                    <button
                      onClick={() => exportCSV(session.id, session.name)}
                      className="p-2 rounded-lg hover:bg-green-100 text-green-600"
                      title="Exporter CSV"
                      data-testid={`export-practice-${session.id}`}
                    >
                      <Download className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => deleteSession(session.id, session.name)}
                      className="p-2 rounded-lg hover:bg-rose-100 text-rose-600"
                      title="Supprimer"
                      data-testid={`delete-practice-${session.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Expanded Stats */}
                {viewingSession === session.id && (
                  <div className="mt-5 pt-5 border-t border-gray-200">
                    {statsLoading ? (
                      <div className="text-center py-8 animate-pulse" style={{ color: '#2E6CF5' }}>Calcul en cours...</div>
                    ) : sessionStats ? (
                      <div>
                        {/* Summary cards */}
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
                          <div className="p-3 rounded-xl bg-gray-50 text-center">
                            <p className="text-xs text-gray-500">Appels LLM</p>
                            <p className="text-2xl font-bold" style={{ color: '#002768' }} data-testid="ps-total-calls">{sessionStats.total_calls}</p>
                          </div>
                          <div className="p-3 rounded-xl bg-gray-50 text-center">
                            <p className="text-xs text-gray-500">Tokens</p>
                            <p className="text-2xl font-bold" style={{ color: '#2E6CF5' }}>{sessionStats.total_tokens.toLocaleString('fr-FR')}</p>
                          </div>
                          <div className="p-3 rounded-xl bg-gray-50 text-center">
                            <p className="text-xs text-gray-500">Étudiants actifs</p>
                            <p className="text-2xl font-bold" style={{ color: '#24B219' }}>{sessionStats.active_students}</p>
                          </div>
                          <div className="p-3 rounded-xl text-center" style={{ backgroundColor: '#FFF3E0' }}>
                            <p className="text-xs text-gray-500">Coût estimé</p>
                            <p className="text-2xl font-bold" style={{ color: '#F4621C' }} data-testid="ps-total-cost">{estimateCost(sessionStats.total_tokens)} $</p>
                          </div>
                        </div>

                        {/* Cost per student */}
                        {sessionStats.active_students > 0 && (
                          <div className="mb-4 p-3 rounded-xl border-2" style={{ borderColor: '#F4621C', backgroundColor: '#FFFAF5' }}>
                            <div className="flex items-center gap-2">
                              <DollarSign className="w-4 h-4" style={{ color: '#F4621C' }} />
                              <span className="text-sm font-medium" style={{ color: '#F4621C' }}>
                                Coût par étudiant actif : {estimateCost(sessionStats.total_tokens / sessionStats.active_students)} $ / étudiant
                              </span>
                            </div>
                          </div>
                        )}

                        {/* Type breakdown */}
                        {Object.keys(sessionStats.by_type).length > 0 && (
                          <div className="flex flex-wrap gap-3 mb-4">
                            {Object.entries(sessionStats.by_type).map(([type, data]) => (
                              <Badge key={type} className="text-white text-xs px-3 py-1" style={{ backgroundColor: TYPE_COLORS[type] || '#999' }}>
                                {TYPE_LABELS[type] || type}: {data.calls} appels ({data.tokens.toLocaleString('fr-FR')} tokens)
                              </Badge>
                            ))}
                          </div>
                        )}

                        {/* Per-student table */}
                        {sessionStats.by_student.length > 0 ? (
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm" data-testid="ps-student-table">
                              <thead>
                                <tr className="border-b-2" style={{ borderColor: '#CAE9FA' }}>
                                  <th className="text-left py-2 px-2 font-semibold text-gray-600">Étudiant</th>
                                  <th className="text-left py-2 px-2 font-semibold text-gray-600">#</th>
                                  <th className="text-center py-2 px-2 font-semibold" style={{ color: '#2E6CF5' }}>Sim.</th>
                                  <th className="text-center py-2 px-2 font-semibold" style={{ color: '#24B219' }}>RAO</th>
                                  <th className="text-center py-2 px-2 font-semibold" style={{ color: '#F4621C' }}>Rétro.</th>
                                  <th className="text-center py-2 px-2 font-semibold text-gray-600">Total</th>
                                  <th className="text-center py-2 px-2 font-semibold text-gray-600">Tokens</th>
                                  <th className="text-center py-2 px-2 font-semibold" style={{ color: '#F4621C' }}>Coût</th>
                                </tr>
                              </thead>
                              <tbody>
                                {sessionStats.by_student.map((s, i) => (
                                  <tr key={s.student_number} className={`border-b ${i % 2 === 0 ? 'bg-gray-50/50' : ''}`}>
                                    <td className="py-2 px-2 font-medium">{s.student_name}</td>
                                    <td className="py-2 px-2 text-gray-500 text-xs">{s.student_number}</td>
                                    <td className="py-2 px-2 text-center">
                                      {s.simulation_911 > 0 && <Badge className="text-white text-xs" style={{ backgroundColor: '#2E6CF5' }}>{s.simulation_911}</Badge>}
                                    </td>
                                    <td className="py-2 px-2 text-center">
                                      {s.rao_variant > 0 && <Badge className="text-white text-xs" style={{ backgroundColor: '#24B219' }}>{s.rao_variant}</Badge>}
                                    </td>
                                    <td className="py-2 px-2 text-center">
                                      {s.retroaction > 0 && <Badge className="text-white text-xs" style={{ backgroundColor: '#F4621C' }}>{s.retroaction}</Badge>}
                                    </td>
                                    <td className="py-2 px-2 text-center font-bold" style={{ color: '#002768' }}>{s.total_calls}</td>
                                    <td className="py-2 px-2 text-center text-gray-500">{s.total_tokens.toLocaleString('fr-FR')}</td>
                                    <td className="py-2 px-2 text-center font-semibold" style={{ color: '#F4621C' }}>{estimateCost(s.total_tokens)} $</td>
                                  </tr>
                                ))}
                              </tbody>
                              <tfoot>
                                <tr className="border-t-2 font-bold" style={{ borderColor: '#002768' }}>
                                  <td className="py-2 px-2" colSpan={5}>Total</td>
                                  <td className="py-2 px-2 text-center" style={{ color: '#002768' }}>{sessionStats.total_calls}</td>
                                  <td className="py-2 px-2 text-center text-gray-500">{sessionStats.total_tokens.toLocaleString('fr-FR')}</td>
                                  <td className="py-2 px-2 text-center" style={{ color: '#F4621C' }}>{estimateCost(sessionStats.total_tokens)} $</td>
                                </tr>
                              </tfoot>
                            </table>
                          </div>
                        ) : (
                          <p className="text-gray-400 text-center py-4">Aucune activité LLM durant cette période</p>
                        )}
                      </div>
                    ) : null}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Comparison View */}
      {compareData && compareData.length >= 2 && (
        <Card className="glass border-0 mt-6" style={{ borderLeft: '4px solid #7C3AED' }}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{ color: '#7C3AED' }}>
              <GitCompare className="w-5 h-5" />
              Comparaison — {compareData.length} séances
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Summary comparison table */}
            <div className="overflow-x-auto mb-6">
              <table className="w-full text-sm" data-testid="compare-table">
                <thead>
                  <tr className="border-b-2" style={{ borderColor: '#7C3AED' }}>
                    <th className="text-left py-3 px-3 font-semibold text-gray-600">Métrique</th>
                    {compareData.map(d => (
                      <th key={d.session.id} className="text-center py-3 px-3 font-semibold" style={{ color: '#002768' }}>
                        <div>{d.session.name}</div>
                        <div className="text-[10px] text-gray-400 font-normal mt-0.5">
                          {d.session.group_name || 'Tous'}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b">
                    <td className="py-2.5 px-3 font-medium">Appels LLM</td>
                    {compareData.map(d => (
                      <td key={d.session.id} className="py-2.5 px-3 text-center font-bold" style={{ color: '#002768' }}>{d.total_calls}</td>
                    ))}
                  </tr>
                  <tr className="border-b bg-gray-50/50">
                    <td className="py-2.5 px-3 font-medium">Tokens estimés</td>
                    {compareData.map(d => (
                      <td key={d.session.id} className="py-2.5 px-3 text-center" style={{ color: '#2E6CF5' }}>{d.total_tokens.toLocaleString('fr-FR')}</td>
                    ))}
                  </tr>
                  <tr className="border-b">
                    <td className="py-2.5 px-3 font-medium">Étudiants actifs</td>
                    {compareData.map(d => (
                      <td key={d.session.id} className="py-2.5 px-3 text-center" style={{ color: '#24B219' }}>{d.active_students}</td>
                    ))}
                  </tr>
                  <tr className="border-b bg-gray-50/50">
                    <td className="py-2.5 px-3 font-medium" style={{ color: '#F4621C' }}>Coût total</td>
                    {compareData.map(d => (
                      <td key={d.session.id} className="py-2.5 px-3 text-center font-bold" style={{ color: '#F4621C' }}>{estimateCost(d.total_tokens)} $</td>
                    ))}
                  </tr>
                  <tr className="border-b">
                    <td className="py-2.5 px-3 font-medium" style={{ color: '#F4621C' }}>Coût / étudiant</td>
                    {compareData.map(d => (
                      <td key={d.session.id} className="py-2.5 px-3 text-center font-bold" style={{ color: '#F4621C' }}>
                        {d.active_students > 0 ? estimateCost(d.total_tokens / d.active_students) : '—'} $
                      </td>
                    ))}
                  </tr>
                  <tr className="border-b bg-gray-50/50">
                    <td className="py-2.5 px-3 font-medium">Moy. appels / étudiant</td>
                    {compareData.map(d => (
                      <td key={d.session.id} className="py-2.5 px-3 text-center">
                        {d.active_students > 0 ? Math.round(d.total_calls / d.active_students) : '—'}
                      </td>
                    ))}
                  </tr>
                  {['simulation_911', 'rao_variant', 'retroaction'].map(type => (
                    <tr key={type} className="border-b">
                      <td className="py-2.5 px-3 font-medium">
                        <span style={{ color: TYPE_COLORS[type] }}>{TYPE_LABELS[type]}</span>
                      </td>
                      {compareData.map(d => (
                        <td key={d.session.id} className="py-2.5 px-3 text-center">
                          {d.by_type[type] ? `${d.by_type[type].calls} (${d.by_type[type].tokens.toLocaleString('fr-FR')} tok.)` : '—'}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Visual bar comparison */}
            <div className="space-y-4">
              <h4 className="text-sm font-semibold text-gray-600">Comparaison visuelle — Coût total</h4>
              {(() => {
                const maxCost = Math.max(...compareData.map(d => d.total_tokens), 1);
                return compareData.map((d, i) => (
                  <div key={d.session.id} className="flex items-center gap-3">
                    <span className="text-sm font-medium w-40 truncate" title={d.session.name}>{d.session.name}</span>
                    <div className="flex-1 bg-gray-100 rounded-full h-7 overflow-hidden relative">
                      <div
                        className="h-full rounded-full transition-all flex items-center justify-end pr-2"
                        style={{
                          width: `${Math.max((d.total_tokens / maxCost) * 100, 4)}%`,
                          backgroundColor: ['#7C3AED', '#2E6CF5', '#F4621C', '#24B219', '#D97706'][i % 5],
                        }}
                      >
                        <span className="text-xs font-bold text-white whitespace-nowrap">{estimateCost(d.total_tokens)} $</span>
                      </div>
                    </div>
                  </div>
                ));
              })()}
            </div>

            {/* Cost per student comparison */}
            <div className="space-y-4 mt-6">
              <h4 className="text-sm font-semibold text-gray-600">Comparaison visuelle — Coût par étudiant</h4>
              {(() => {
                const maxCostPerStudent = Math.max(
                  ...compareData.map(d => d.active_students > 0 ? d.total_tokens / d.active_students : 0), 1
                );
                return compareData.map((d, i) => {
                  const costPerStudent = d.active_students > 0 ? d.total_tokens / d.active_students : 0;
                  return (
                    <div key={d.session.id} className="flex items-center gap-3">
                      <span className="text-sm font-medium w-40 truncate" title={d.session.name}>{d.session.name}</span>
                      <div className="flex-1 bg-gray-100 rounded-full h-7 overflow-hidden relative">
                        <div
                          className="h-full rounded-full transition-all flex items-center justify-end pr-2"
                          style={{
                            width: `${Math.max((costPerStudent / maxCostPerStudent) * 100, 4)}%`,
                            backgroundColor: ['#7C3AED', '#2E6CF5', '#F4621C', '#24B219', '#D97706'][i % 5],
                          }}
                        >
                          <span className="text-xs font-bold text-white whitespace-nowrap">
                            {estimateCost(costPerStudent)} $ / étu.
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PracticeSessionsTab;
